"use client";

import Link from "next/link";
import { AssistantMark } from "./AssistantMark";
import type { NavLink } from "@/lib/pages/site-schema";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { logoutAction } from "@/app/[lang]/(auth)/login/actions";
import { langPath, segmentForLocale, swapLang, type Lang } from "@/lib/i18n";
import { languageName } from "./LangToggle";
import { cn } from "@/components/ui/cn";

type Props = {
  lang: Lang;
  isAuthed: boolean;
  accountLabel: string;
  loginHref: string;
  links: NavLink[];
  /** Enabled portal locales (O8) — the drawer is the phone's only language
   *  switch (the header's LangToggle lives in the md+ actions cluster). */
  languages: string[];
  tone?: "light" | "dark";
  labels: {
    login: string;
    register: string;
    open: string;
    close: string;
    profile: string;
    logout: string;
  };
};

export function NavMobileToggle({
  lang,
  isAuthed,
  accountLabel,
  loginHref,
  links,
  languages,
  tone = "light",
  labels,
}: Props) {
  const [open, setOpen] = useState(false);
  const pathname = usePathname() ?? `/${lang}`;

  // Escape closes the drawer (keyboard parity with the AppShell drawer).
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls="mobile-nav"
        className={
          "md:hidden inline-flex items-center justify-center w-12 h-12 transition-colors " +
          (tone === "dark" ? "text-teal" : "text-white")
        }
      >
        <span className="sr-only">{open ? labels.close : labels.open}</span>
        <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
          {open ? (
            <path
              d="M6 6l12 12M6 18L18 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              fill="none"
            />
          ) : (
            <path
              d="M4 7h16M4 12h16M4 17h16"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              fill="none"
            />
          )}
        </svg>
      </button>

      {open ? (
        <div
          id="mobile-nav"
          className="md:hidden absolute inset-x-0 top-full bg-teal border-t border-[#1E2A33] z-40"
        >
          <ul className="flex flex-col py-2">
            {links.map((l) => (
              <li key={l.href}>
                <Link
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className={cn(
                    "block px-6 py-3 text-white hover:text-gold",
                    l.assistant && "flex items-center gap-2",
                  )}
                >
                  {l.assistant ? <AssistantMark /> : null}
                  {l.label}
                </Link>
              </li>
            ))}
            <li className="border-t border-[#1E2A33] mt-2 pt-2">
              {isAuthed ? (
                <>
                  <Link
                    href={langPath(lang, "/profil")}
                    onClick={() => setOpen(false)}
                    className="block px-6 py-3 text-gold"
                  >
                    {accountLabel || labels.profile}
                  </Link>
                  <form action={logoutAction}>
                    <input type="hidden" name="lang" value={lang} />
                    <button
                      type="submit"
                      className="block w-full text-left px-6 py-3 text-white hover:text-gold"
                    >
                      {labels.logout}
                    </button>
                  </form>
                </>
              ) : (
                <>
                  <Link
                    href={loginHref}
                    onClick={() => setOpen(false)}
                    className="block px-6 py-3 text-white hover:text-gold"
                  >
                    {labels.login}
                  </Link>
                  <Link
                    href={langPath(lang, "/register")}
                    onClick={() => setOpen(false)}
                    className="block px-6 py-3 text-gold"
                  >
                    {labels.register}
                  </Link>
                </>
              )}
            </li>
            {languages.length > 1 ? (
              <li className="border-t border-[#1E2A33] mt-2 px-6 py-4">
                <span role="group" aria-label="Language" className="flex flex-wrap gap-2">
                  {languages.map((locale) => {
                    const seg = segmentForLocale(locale);
                    const langPart = locale.split("_")[0] ?? locale;
                    return (
                      <Link
                        key={locale}
                        href={swapLang(pathname, seg)}
                        hrefLang={langPart}
                        lang={langPart}
                        title={languageName(langPart)}
                        aria-current={lang === seg ? "true" : undefined}
                        onClick={() => setOpen(false)}
                        className={cn(
                          "rounded-pill border px-3 py-1.5 text-xs font-bold transition-colors",
                          lang === seg
                            ? "bg-gold text-accent-foreground border-gold"
                            : "border-white/30 text-white/85 hover:border-white/60 hover:text-white",
                        )}
                      >
                        {seg}
                      </Link>
                    );
                  })}
                </span>
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}
    </>
  );
}
